import { CdkOverlayOrigin } from '@angular/cdk/overlay'
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  Signal,
  TemplateRef,
  computed,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { ActivatedRoute, Router } from '@angular/router'

import { ColDef } from 'ag-grid-community'
import { MenuItem } from 'primeng/api'
import { MenuModule } from 'primeng/menu'

import { EMPTY, Observable, of, switchMap } from 'rxjs'
import { catchError, filter, map, take, tap } from 'rxjs/operators'

import { FlxButtonComponent, FlxModalService } from '@flowx/angular-ui-toolkit'
import { AnalyticsService } from '@flowx/designer/modules/analytics/analytics.service'
import { FlxContentValueEditComponent } from '@flowx/designer/modules/cms/pages/content-value-edit/content-value-edit.component'
import { FlxClickStopPropagationDirective } from '@flowx/designer/modules/core/directives/stop-propagation.directive'
import { SystemStore } from '@flowx/designer/modules/integration-designer/components/systems-page/systems.store'
import { FlxResourceHeaderComponent } from '@flowx/designer/modules/layout/components/resource-header/resource-header.component'
import { FlxNewEnumerationModalComponent } from '@flowx/designer/modules/projects/components/new-enumeration-modal/new-enumeration-modal.component'
import {
  DefaultActionsColumn,
  FlxResourcesGridComponent,
  getActionsColumnLength,
} from '@flowx/designer/modules/projects/components/resources-grid/resources-grid.component'
import {
  AuditDefaultMenuItem,
  CopyResourceDefaultMenuItem,
  DeleteDefaultMenuItem,
  DetailsDefaultMenuItem,
  DocumentationDefaultMenuItem,
  DuplicateDefaultMenuItem,
  ResourceHistoryDefaultMenuItem,
  compareMenuItemsByOrder,
  withMenuAnalytics,
} from '@flowx/designer/modules/projects/components/resources-page/menu-items.constants'
import { ApplicationDetailsStore } from '@flowx/designer/modules/projects/store/application-version-details.store'
import { DetailedPermissions } from '@flowx/designer/modules/workspaces/shared/workspace.utils'
import { ActionsCellComponent } from '@flowx/designer/shared/components/ag-grid/cell-components/actions-cell/actions-cell.component'
import { AuditLogsComponent } from '@flowx/designer/shared/components/audit-logs/audit-logs.component'
import { FlxBreadcrumbItem } from '@flowx/designer/shared/components/breadcrumbs/breadcrumbs.component'
import { ConfirmationModalComponent } from '@flowx/designer/shared/components/confirmation-modal/confirmation-modal.component'
import { CopyResourceComponent } from '@flowx/designer/shared/components/copy-resource/copy-resource.component'
import { DuplicateResourceComponent } from '@flowx/designer/shared/components/duplicate-resource/duplicate-resource.component'
import { FlxOverlayComponent } from '@flowx/designer/shared/components/overlay/overlay.component'
import { ResourceDocumentationService } from '@flowx/designer/shared/components/resource-documentation/resource-documentation.service'
import { ResourceUsagesDeleteConfirmationComponent } from '@flowx/designer/shared/components/resource-usages/resource-usages-delete-confirmation.component'
import { ResourceUsagesComponent } from '@flowx/designer/shared/components/resource-usages/resource-usages.component'
import { ApplicationResourceType } from '@flowx/designer/shared/entities/application.entity'
import {
  ContentValue,
  Enumeration,
  EnumerationToastMessages,
  newEnumeration,
  prepareForTable,
} from '@flowx/designer/shared/entities/enumeration.entity'
import { ManifestResourceType } from '@flowx/designer/shared/entities/manifest-resource-type.entity'
import {
  CopyResourceParams,
  CopyResourceStep,
} from '@flowx/designer/shared/model/copy-resource.model'
import {
  ResourceUsage,
  ResourceUsageDeleteTemplateData,
  buildResourceDeleteConfirmation,
} from '@flowx/designer/shared/model/resource-usage.model'
import { ComponentTypeEnum, ElementTypeEnum } from '@flowx/designer/shared/services/audit.service'
import { EnumerationsService } from '@flowx/designer/shared/services/enumerations.service'
import { ResourceHistoryModalService } from '@flowx/designer/shared/services/resource-history-modal.service'
import { ResourceUsagesService } from '@flowx/designer/shared/services/resource-usages.service'
import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'

@Component({
  selector: 'app-enumeration-values',
  templateUrl: './enumeration-values.component.html',
  styleUrls: ['./enumeration-values.component.scss'],
  providers: [SystemStore],
  host: { class: 'flex flex-col h-full' },
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FlxResourceHeaderComponent,
    ResourceUsagesDeleteConfirmationComponent,
    FlxButtonComponent,
    FlxClickStopPropagationDirective,
    CdkOverlayOrigin,
    FlxOverlayComponent,
    FlxResourcesGridComponent,
    AuditLogsComponent,
    MenuModule,
  ],
})
export class EnumerationValuesComponent implements OnInit, OnDestroy {
  #destroyRef = inject(DestroyRef)
  #route = inject(ActivatedRoute)
  #router = inject(Router)
  #enumerationsService = inject(EnumerationsService)
  #resourceDocumentation = inject(ResourceDocumentationService)
  #flxModalService = inject(FlxModalService)
  #path: Record<string, any>[] = []
  #applicationDetailsStore = inject(ApplicationDetailsStore)
  #systemStore = inject(SystemStore)
  #resourceHistoryModalService = inject(ResourceHistoryModalService)
  #resourceUsagesService = inject(ResourceUsagesService)
  #toastMessageService = inject(ToastMessageService)
  #analyticsService = inject(AnalyticsService)

  private resourceUsagesDeleteTRef =
    viewChild<TemplateRef<ResourceUsageDeleteTemplateData>>('resourceUsagesDelete')

  protected ComponentTypeEnum = ComponentTypeEnum
  protected ElementTypeEnum = ElementTypeEnum
  // PERMISSIONS
  accessRights = input<DetailedPermissions>()

  languages = this.#applicationDetailsStore.languages
  sourceSystems = this.#systemStore.systems

  enumerationValuesTableCols: Signal<ColDef[]> = computed(() => {
    const languages = this.languages()
    const sourceSystems = this.sourceSystems()
    const enumeration = this.enumeration()
    const readOnly = this.readOnly()
    const accessRights = this.accessRights()

    let maxTotalActions = 1

    if (accessRights.canEdit) {
      maxTotalActions += 2
    }

    return [
      {
        headerName: 'Code',
        field: 'code',
        minWidth: 80,
      },
      {
        headerName: 'Order',
        field: 'order',
        minWidth: 80,
      },
      ...languages.map((item) => ({ headerName: item.code, field: item.code })),
      ...sourceSystems.map((system) => ({ headerName: system.name, field: system.code })),
      {
        ...DefaultActionsColumn,
        width: getActionsColumnLength(maxTotalActions),
        cellRenderer: ActionsCellComponent<Enumeration>,
        cellRendererParams: (params) => ({
          actions: [
            ...(params.data.childContentDescription
              ? [
                  {
                    icon: 'ph-arrow-arc-right',
                    tooltip: 'Open',
                    clickAction: (contentValue: ContentValue) => this.goTo(contentValue),
                    dataTestId: 'open-child-enumeration-button',
                  },
                ]
              : []),
            ...(accessRights.canEdit && !params.data.childContentDescription
              ? [
                  {
                    icon: 'ph-folder-plus',
                    disabled: readOnly,
                    tooltip: 'Create child enumeration',
                    clickAction: (contentValue: ContentValue) =>
                      this.createChildEnumeration(this.enumeration(), contentValue),
                    dataTestId: 'create-child-enumeration-button',
                  },
                ]
              : []),
            ...(accessRights.canEdit
              ? [
                  {
                    icon: readOnly ? 'ph-eye' : 'ph-pencil-simple',
                    disabled: readOnly,
                    tooltip: 'Details',
                    dataTestId: 'edit-button',
                    clickAction: (contentValue: ContentValue) =>
                      this.openContentValueEditModal(enumeration, contentValue, readOnly),
                  },
                  {
                    icon: 'ph-trash-simple',
                    tooltip: '',
                    disabled: readOnly,
                    dataTestId: 'delete-button',
                    clickAction: (contentValue: ContentValue, event: Event) =>
                      this.deleteEnumerationValue(event, enumeration, contentValue),
                  },
                ]
              : []),
          ],
        }),
      },
    ]
  })

  contextMenuItems: Signal<MenuItem[]> = computed(() => {
    const accessRights = this.accessRights()
    const readOnly = this.readOnly()
    const canEditDetails = accessRights.canEdit && !readOnly
    const isRoot = this.isRoot()
    return withMenuAnalytics(
      [
        {
          ...AuditDefaultMenuItem(false, true),
          command: () => this.showAuditLogsOverlay.set(true),
        },
        {
          ...DuplicateDefaultMenuItem(readOnly, accessRights.canEdit && isRoot),
          command: () => this.#openDuplicateModal(),
        },
        {
          ...DetailsDefaultMenuItem(false, isRoot, !canEditDetails),
          command: (): void => this.onRenameEnumeration(!canEditDetails),
        },
        {
          ...DocumentationDefaultMenuItem(false, isRoot),
          command: (): void => this.#openDocumentation(!canEditDetails),
        },
        {
          ...CopyResourceDefaultMenuItem(false, isRoot),
          command: (): void => this.#openCopyModal(),
        },
        {
          ...ResourceHistoryDefaultMenuItem(),
          analyticsProps: { source: 'rowAction' },
          command: (): void => this.#openResourceHistoryModal(),
        },
        {
          ...DeleteDefaultMenuItem(readOnly, accessRights.canDelete && isRoot),
          command: () => this.#delete(),
        },
      ].sort(compareMenuItemsByOrder),
      'enumeration_value',
      this.#analyticsService
    )
  })

  emptyMsg = computed(() => {
    const mappedEnumeration = this.mappedEnumeration()

    if (!mappedEnumeration?.description) {
      return ''
    }

    return 'No values yet'
  })

  showAuditLogsOverlay = signal(false)
  enumeration = toSignal(this.#enumerationsService.enumeration)
  mappedEnumeration = computed(() => prepareForTable(this.enumeration()))

  breadcrumbItems: FlxBreadcrumbItem[]
  rootId: string
  enumerationId = signal('')
  enumerationName = signal('')
  readOnly = this.#applicationDetailsStore.isVersionCommitted
  appVersionId = this.#applicationDetailsStore.appVersionId

  isRoot = computed(() => {
    const enumerationId = this.enumerationId()
    const rootId = this.rootId
    return !!enumerationId && enumerationId === rootId
  })

  ngOnInit(): void {
    this.#systemStore.loadAll(true)
    this.#route.queryParamMap
      .pipe(
        tap((queryParamMap) => {
          // TODO find a better way to to this..
          this.#path = JSON.parse(queryParamMap.get('path'))
          if (!this.#path || !this.#path.length) {
            return
          }
          this.enumerationId.set(this.#path[this.#path.length - 1].id)
          this.rootId = this.#path[0].id
          this.#enumerationsService
            .getEnumeration(this.rootId, this.enumerationId())
            .pipe(take(1))
            .subscribe()
          this.breadcrumbItems = this.#buildBreadcrumbs(this.#path)
          this.enumerationName.set(this.breadcrumbItems[this.breadcrumbItems.length - 1].label)
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe()
  }

  openContentValueEditModal(
    enumeration: Enumeration,
    toBeUpdatedContentValue?: ContentValue,
    readOnly?: boolean
  ): void {
    // Calculate the current maximum order value
    const contentValues = enumeration?.contentValues || []
    const orderValues = contentValues.map((value) => value.order || 0)
    const currentMaxOrder = orderValues.length ? Math.max(...orderValues) : 0

    this.#flxModalService
      .show(
        FlxContentValueEditComponent,
        {
          closeOnBackdropClick: false,
        },
        {
          languages: this.languages(),
          sourceSystems: this.sourceSystems(),
          contentValue: toBeUpdatedContentValue,
          readOnly,
          type: 'enumeration',
          isNew: !toBeUpdatedContentValue,
          currentMaxOrder,
        }
      )
      .data$.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((data) => {
        if (data) {
          this.#createUpdateEnumerationValue(data, enumeration, toBeUpdatedContentValue)
        }
      })
  }

  deleteEnumerationValue(
    event: Event,
    enumeration: Enumeration,
    enumerationValue: ContentValue
  ): void {
    event.stopPropagation()
    const modal = this.#flxModalService.show(
      ConfirmationModalComponent,
      {
        closeOnBackdropClick: false,
        backdropClass: 'overlay-backdrop',
      },
      {
        title: `Delete enumeration value <span class="font-bold">${enumerationValue.code}</span>`,
        subtitle: `Deleting this value will affect any processes that use it.`,
        btnOkText: 'Delete',
        btnCancelText: 'Cancel',
      }
    )

    modal.data$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((data) => {
      if (data.confirm) {
        this.#enumerationsService.deleteEnumerationValue(this.rootId, enumeration, enumerationValue)
      }
    })
  }

  createChildEnumeration(enumeration: Enumeration, enumerationValue: ContentValue): void {
    this.#enumerationsService
      .createChildEnumeration(
        this.rootId,
        newEnumeration({
          description: { name: `${enumeration.description.name}-${enumerationValue.code}` },
        }),
        enumeration,
        enumerationValue.valueId
      )
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        map((newEnumeration) => ({
          ...enumeration,
          contentValues: [
            ...enumeration.contentValues.map((contentValue) => {
              if (contentValue.valueId === enumerationValue.valueId) {
                return {
                  ...contentValue,
                  childContentDescription: {
                    ...newEnumeration.description,
                    childContentId: newEnumeration.id,
                  },
                }
              }
              return contentValue
            }),
          ],
        })),
        tap((newEnumeration) => {
          const foundEnumerationValue = newEnumeration.contentValues.find(
            (newEnumerationValue) => newEnumerationValue.valueId === enumerationValue.valueId
          )

          if (foundEnumerationValue) {
            this.goTo(foundEnumerationValue as ContentValue)
          }
        })
      )
      .subscribe()
  }

  goTo(enumerationValue: ContentValue): void {
    if (enumerationValue.childContentDescription?.name) {
      this.navigate(
        enumerationValue.childContentDescription?.version,
        enumerationValue.childContentDescription.name,
        enumerationValue.childContentDescription.childContentId,
        this.#path
      )
    }
  }

  navigate(version: number, name: string, id: string, path: any): void {
    this.#router.navigate([], {
      relativeTo: this.#route,
      queryParams: {
        version,
        path: JSON.stringify([
          ...path,
          {
            version,
            name,
            id,
          },
        ]),
      },
      queryParamsHandling: 'merge',
    })
  }

  ngOnDestroy(): void {
    this.#enumerationsService.resetValues()
  }

  onRenameEnumeration(readOnly: boolean): void {
    const enumeration = this.enumeration()
    const modal = this.#flxModalService.show(
      FlxNewEnumerationModalComponent,
      {
        closeOnBackdropClick: false,
      },
      {
        enumeration,
        readOnly,
      }
    )

    modal.data$
      .pipe(
        tap((e) => {
          this.#updateEnumeration(e)
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe()
  }
  openResourceUsagesModal(
    enumeration: Enumeration,
    source: 'rowAction' | 'pageAction' | 'fromDeleteConfirm' = 'pageAction',
    resourceUsages?: ResourceUsage[]
  ): void {
    let endpoint: Observable<ResourceUsage[]>

    if (!resourceUsages) {
      endpoint = this.#resourceUsagesService.getResourceUsages(enumeration.id)
    } else {
      endpoint = of(resourceUsages)
    }

    endpoint.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((resourceUsages) =>
      this.#flxModalService.show(
        ResourceUsagesComponent,
        {},
        {
          name: enumeration.description.name,
          resourceType: ApplicationResourceType.Enumeration,
          resourceUsages,
          source,
        }
      )
    )
  }
  #updateEnumeration(enumeration: Enumeration): void {
    this.#enumerationsService
      .updateApplicationEnumeration(enumeration)
      .pipe(
        tap(() => {
          this.#toastMessageService.showSuccessMessage(
            EnumerationToastMessages.UPDATE_ENUMERATION_SUCCESS
          )
          this.#flxModalService.hide()
        }),
        catchError((error) => {
          this.#toastMessageService.showErrorMessage(
            error.title ?? EnumerationToastMessages.UPDATE_ENUMERATION_FAILURE
          )
          return EMPTY
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe()
  }

  #buildBreadcrumbs(path: Record<string, any>[]): FlxBreadcrumbItem[] {
    return [
      {
        label: 'Enumerations',
        command: () => this.#router.navigate(['../'], { relativeTo: this.#route }),
      },
      ...path.map((pathFragment) => ({
        label: pathFragment.name,
        command: (event) => {
          const index = this.#path.findIndex((_item) => _item.name === event.item.label)
          const pathDetails = this.#path.slice(0, index + 1)
          const firstPath = pathDetails[0]
          if (firstPath) {
            this.#router.navigate([], {
              relativeTo: this.#route,
              queryParams: {
                version: firstPath.version,
                path: JSON.stringify([...pathDetails]),
                id: firstPath.id,
              },
              queryParamsHandling: 'merge',
            })
          }
        },
      })),
    ]
  }

  #createUpdateEnumerationValue(
    contentValue: ContentValue,
    enumeration: Enumeration,
    toBeUpdatedContentValue?: ContentValue
  ): void {
    if (toBeUpdatedContentValue) {
      this.#enumerationsService
        .updateEnumerationValue(this.rootId, enumeration, contentValue)
        .pipe(
          takeUntilDestroyed(this.#destroyRef),
          switchMap(() =>
            this.#enumerationsService
              .getEnumeration(this.rootId, this.enumerationId())
              .pipe(take(1))
          ),
          tap(() => {
            this.#flxModalService.hide()
          })
        )
        .subscribe()
    } else {
      this.#enumerationsService
        .createEnumerationValue(this.rootId, enumeration, contentValue)
        .pipe(
          takeUntilDestroyed(this.#destroyRef),
          switchMap(() =>
            // Refresh the enumeration list after creating
            this.#enumerationsService
              .getEnumeration(this.rootId, this.enumerationId())
              .pipe(take(1))
          ),
          tap(() => this.#flxModalService.hide())
        )
        .subscribe()
    }
  }

  #openResourceHistoryModal(): void {
    const enumeration = this.enumeration()
    this.#resourceHistoryModalService.toggleResourceHistoryModal({
      resourceDefinitionId: enumeration.resourceDefinitionId,
      resourceType: ManifestResourceType.Enumeration,
      resourceName: enumeration.description.name,
    })
  }

  #openDuplicateModal(): void {
    const enumeration = this.enumeration()
    const modal = this.#flxModalService.show(
      DuplicateResourceComponent,
      {},
      {
        resourceName: enumeration.description.name,
        params: {
          appId: this.#applicationDetailsStore.appId(),
          appVersionId: this.#applicationDetailsStore.appVersionId(),
          resourceId: enumeration.id,
          resourceType: ApplicationResourceType.Enumeration,
        } satisfies CopyResourceParams,
      }
    )
    modal.data$
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        tap(() => {
          this.#router.navigate(['../'], { relativeTo: this.#route })
        })
      )
      .subscribe()
  }

  #openCopyModal(): void {
    const enumeration = this.enumeration()
    this.#flxModalService.show(
      CopyResourceComponent,
      {},
      {
        stepName: CopyResourceStep.SELECT_DESTINATION,
        sourceAppName: this.#applicationDetailsStore.appName(),
        resourceName: enumeration.description.name,
        params: {
          appId: this.#applicationDetailsStore.appId(),
          appVersionId: this.#applicationDetailsStore.appVersionId(),
          resourceId: enumeration.id,
          resourceType: ApplicationResourceType.Enumeration,
        } satisfies CopyResourceParams,
      }
    )
  }

  #delete(): void {
    const enumeration = this.enumeration()
    this.#resourceUsagesService
      .getResourceUsages(enumeration.id)
      .pipe(
        switchMap((resourceUsages) =>
          this.#flxModalService
            .show(
              ConfirmationModalComponent,
              {
                closeOnBackdropClick: false,
                backdropClass: 'overlay-backdrop',
              },
              {
                enumeration,
                ...buildResourceDeleteConfirmation(
                  enumeration.description.name,
                  ApplicationResourceType.Enumeration,
                  resourceUsages.length > 0
                ),
                resourceUsages,
                customTemplate: this.resourceUsagesDeleteTRef(),
              }
            )
            .data$.pipe(
              filter((data) => data.confirm),
              switchMap(() =>
                this.#enumerationsService
                  .deleteApplicationEnumeration(enumeration.id, enumeration)
                  .pipe(
                    tap(() => {
                      this.#toastMessageService.showSuccessMessage(
                        EnumerationToastMessages.DELETE_ENUMERATION_SUCCESS
                      )
                      this.#router.navigate(['../'], { relativeTo: this.#route })
                    }),
                    catchError((error) => {
                      this.#toastMessageService.showErrorMessage(
                        error.title ?? EnumerationToastMessages.DELETE_ENUMERATION_FAILURE
                      )

                      return EMPTY
                    })
                  )
              )
            )
        ),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe()
  }

  #openDocumentation(readOnly: boolean): void {
    const enumeration = this.enumeration()

    this.#resourceDocumentation
      .open({
        resourceType: ApplicationResourceType.Enumeration,
        resourceId: enumeration.id,
        resourceName: enumeration.description.name,
        readOnly,
      })
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(({ description }) =>
        this.#enumerationsService.updateEnumerationTable({
          ...this.enumeration(),
          resourceDescription: { ...this.enumeration().resourceDescription, description },
        })
      )
  }
}
